/**
 * MotionCard — Card with atmospheric hover and spring-physics feedback.
 *
 * - Hover: lift with glowing shadow and subtle border luminance
 * - Press: scale feedback with spring physics
 * - Smooth cubic-bezier transitions
 */

import { m } from "framer-motion";
import * as React from "react";
import { cn } from "@/lib/utils";

interface MotionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  disableMotion?: boolean;
}

export const MotionCard = React.forwardRef<HTMLDivElement, MotionCardProps>(
  ({ className, disableMotion, children, ...props }, ref) => {
    if (disableMotion) {
      return (
        <div
          ref={ref}
          className={cn(
            "rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/80 backdrop-blur-sm text-slate-900 dark:text-slate-100 shadow-sm transition-all duration-300",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      );
    }

    return (
      <m.div
        ref={ref}
        whileHover={{
          y: -3,
          boxShadow: "0 12px 30px -8px rgba(0, 0, 0, 0.1), 0 4px 12px -4px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(59, 122, 255, 0.04)",
          transition: { type: "spring", stiffness: 400, damping: 25 },
        }}
        whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
        className={cn(
          "rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/80 backdrop-blur-sm text-slate-900 dark:text-slate-100 shadow-sm",
          className,
        )}
        {...(props as React.ComponentPropsWithoutRef<typeof m.div>)}
      >
        {children}
      </m.div>
    );
  },
);
MotionCard.displayName = "MotionCard";
